// LandingPage.js
import "./LandingPage.css";
import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FaChartLine, FaBullseye, FaComments } from "react-icons/fa";
import "../components/Navbar.css"

const HomePage = () => {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const INDUSTRIES = [
    "Beauty & Salons",
    "Retail",
    "Financial Services",
    "Hospitality",
    "Healthcare",
    "Fitness & Wellness",
    "Education",
    "Real Estate",
    "E-Commerce",
  ];

  return (
    <>
      <Navbar />


      <div
        className="landing-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "48px 20px",
          gap: 32,
        }}
      >
        {/* HERO — full-bleed white with animated background */}
        <section
          className="charts-bg" // uses your existing moving graphics
          style={{
            /* make the section span the full viewport width even inside a centered layout */
            width: "100vw",
            margin: "0 calc(50% - 50vw)",
            background: "#ffffff",       // only this section is white
            border: "none",
            borderRadius: 0,
            boxShadow: "none",
            padding: "64px 20px 48px",
          }}
        >
          {/* Inner container to keep content nicely centered and readable */}
          <div
            style={{
              maxWidth: 1200,
              margin: "0 auto",
              display: "grid",
              gap: 18,
              justifyItems: "center",
              textAlign: "center",
            }}
          >
            <div className="badge">Purpose-built for small businesses</div>

            <h1 className="section-title hero-title" style={{ color: "#0f172a" }}>
              From Spreadsheet Stress to AI-Blessed
            </h1>

            <p className="hero-subtext" style={{ color: "#475569" }}>
            Your business deserves its own AI Data Assistant. Meet Taren — smart, personalized, 
            and built around the way you already work. Upload files, 
            ask questions in plain English, and get simple, trustworthy insights fast.
            <br /><br />
            </p>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {/* exact functionality preserved */}
              <button
                className="cta-button"
                onClick={() => navigate("/auth?mode=signup")}
              >
                Get Started Today
              </button>
              <button
                className="cta-button"
                style={{ background: "transparent", border: "1px solid var(--stroke)", color: "var(--heading)" }}
                onClick={() => scrollTo("features")}
              >
                See what you’ll get
              </button>
            </div>
          </div>

          {/* subtle bottom divider to separate hero from the next section */}
          <div
            aria-hidden="true"
            style={{
              height: 1,
              marginTop: 28,
              width: "min(1200px, 92%)",
              marginLeft: "auto",
              marginRight: "auto",
              background:
                "linear-gradient(90deg, rgba(99,102,241,.35), rgba(99,102,241,.10) 50%, rgba(99,102,241,.35))",
              borderRadius: 1,
            }}
          />
        </section>


          {/* INDUSTRIES (unique, grid layout) */}
          <section className="industries-wrap fade-in" style={{ width: "100%" }}>
            <p className="industries-caption">These industries have their own model — and more to come.</p>

            {(() => {
              const UNIQUE_INDUSTRIES = Array.from(new Set(INDUSTRIES)); // dedupe
              return (
                <div className="industries-grid" aria-label="Supported industries">
                  {UNIQUE_INDUSTRIES.map((name) => (
                    <span key={name} className="industry-chip">
                      {name}
                    </span>
                  ))}
                </div>
              );
            })()}
          </section>


        {/* HOW IT WORKS */}
        <section
          className="instructions-section centered fade-in"
          style={{ width: "100%", maxWidth: 960 }}
        >
          <h2>How it works</h2>
          <p className="instructions-sub">
            Fast setup. Real answers. Keep your existing tools.
          </p>
          <ol
            className="feature-bullets"
            style={{
              listStyle: "none",
              counterReset: "step",
              margin: "0 auto",
              maxWidth: 760,
              textAlign: "left",
            }}
          >
            <li style={{ marginBottom: 10 }}>
              <span className="step-dot">1</span>
              <strong> Upload your files</strong>{" "}
              <span className="muted">
                — CSV/Excel or POS exports. Taren handles cleanup automatically.
              </span>
            </li>
            <li style={{ marginBottom: 10 }}>
              <span className="step-dot">2</span>
              <strong> Ask in plain English</strong>{" "}
              <span className="muted">
                — “What’s driving our busiest days?” “Which service has the best
                margin?”
              </span>
            </li>
            <li>
              <span className="step-dot">3</span>
              <strong> Get actionable insights</strong>{" "}
              <span className="muted">
                — Trends, outliers, top drivers, and weekly summaries.
              </span>
            </li>
          </ol>
        </section>

        {/* WHAT YOU’LL GET */}
        <section
          id="features"
          className="features-section fade-in"
          style={{ width: "100%", maxWidth: 1120 }}
        >
          <h2 className="section-title" style={{ marginBottom: 12 }}>
            What you’ll get
          </h2>
          <div className="features">
            <article className="feature-card">
              <h3 className="price-title">
                <FaChartLine style={{ verticalAlign: "-2px" }} /> Instant cleanup & stats
              </h3>
              <ul className="feature-bullets">
                <li>Automatic data cleaning (CSV/Excel)</li>
                <li>Descriptive metrics & quick checks</li>
                <li>Outlier detection</li>
              </ul>
              <div className="divider" />
              <small className="price-note">Clean, trusted baseline—fast.</small>
            </article>

            <article className="feature-card">
              <h3 className="price-title">
                <FaBullseye style={{ verticalAlign: "-2px" }} /> Trends & drivers
              </h3>
              <ul className="feature-bullets">
                <li>Time trends & control charts</li>
                <li>Correlation heatmaps</li>
                <li>“Top predictor” spotlight</li>
              </ul>
              <div className="divider" />
              <small className="price-note">
                See what actually moves revenue and bookings.
              </small>
            </article>

            <article className="feature-card">
              <h3 className="price-title">
                <FaComments style={{ verticalAlign: "-2px" }} /> Ask AI like a teammate
              </h3>
              <ul className="feature-bullets">
                <li>Plain-English questions</li>
                <li>Industry-aware suggestions</li>
                <li>Weekly summaries (email-ready)</li>
              </ul>
              <div className="divider" />
              <small className="price-note">Answers in under a minute.</small>
            </article>
          </div>
        </section>

          {/* DEMO VIDEO */}
        <section className="landing-section">
          <div className="landing-content centered">
            <h2 className="section-title">Taren in Action</h2>
            <div className="divider" />
            <video className="landing-video" controls autoPlay muted loop>
              <source src="/DemoVid.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </section>


        {/* PRICING */}
        <section
          id="pricing"
          className="pricing-section fade-in"
          aria-labelledby="pricing-title"
          style={{ width: "100%", maxWidth: 1120 }}
        >
          <div className="pricing-header" style={{ marginBottom: 16 }}>
            <h2 id="pricing-title">Simple pricing</h2>
            <p className="sub">
              Start with Taren access. Add POS or other sources when you’re ready.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(260px,1fr))",
              gap: 18,
              alignItems: "stretch",
              maxWidth: 980,
              margin: "0 auto",
            }}
          >
            {/* Plan 1 */}
            <article className="price-card popular">
              <span className="price-badge">Most popular</span>
              <div className="price-title">Taren access</div>
              <div className="price-stack">
                <div className="price-row">
                  <div className="price-label">Monthly</div>
                  <div className="price-price">
                    $24.99 <small>/ mo</small>
                  </div>
                </div>
              </div>
              <div className="divider" />
              <ul className="price-features">
                <li>File uploads (CSV/Excel)</li>
                <li>AI Q&A + Insights</li>
                <li>Trends, drivers, & charts</li>
                <li>Email-ready summaries</li>
              </ul>
              <div style={{ marginTop: 16 }}>
                <button
                  className="price-cta"
                  onClick={() => navigate("/auth?mode=signup")}
                  data-plan="taren-access"
                >
                  Get Taren access
                </button>
              </div>
            </article>

            {/* Plan 2 */}
          <article className="price-card premium centered">
            <span className="price-badge">Custom</span>
            <div className="price-title">Custom solutions</div>

            <p className="price-note" style={{ marginTop: 8 }}>
              Have POS/CRM or unique data needs? We’ll tailor the pipeline, prompts, and refresh schedule to your business.
            </p>

            <div className="divider" />

            <ul className="price-features" style={{ textAlign: "left", display: "inline-block" }}>
              <li>Connect POS, CRM, or niche sources</li>
              <li>Industry-tuned prompts & workflows</li>
              <li>Automated refresh & monitoring</li>
              <li>Priority support</li>
            </ul>

            <div style={{ marginTop: 16 }}>
              <button
                className="price-cta"
                data-plan="custom-solutions"
                onClick={() => {
                  const el = document.getElementById("contact");
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  } else {
                    // fallback if you have a /contact route
                    if (typeof navigate === "function") navigate("/about");
                    else window.location.href = "/about";
                  }
                }}
              >
                Contact us
              </button>
            </div>
          </article>

          </div>

          <p className="price-note" style={{ marginTop: 16 }}>
            You can start with files only and add a POS or other source later—no lock-in.
          </p>
        </section>

        {/* FINAL CTA */}
        <section
          className="custom-saas-section fade-in"
          style={{ width: "100%", maxWidth: 960 }}
        >
          <h2>Ready to see your hidden moneymaker?</h2>
          <p>Upload a file, ask a question, get an answer. It’s that simple.</p>
          <button
            className="cta-button"
            onClick={() => navigate("/auth?mode=signup")}
          >
            Get started
          </button>
        </section>

        <div className="landing-footer">
          <Footer />
        </div>
      </div>
    </>
  );
};

export default HomePage;
